import { useQueryParams } from '@hooks/queryParams';
import { useIsPhone } from '@hooks/useIsPhone';
import { formatDate } from '@utils/dateHelpers';
import {
  WorkloadDraftsItems,
  WorkloadMonthlyCalendarItems,
  WorkloadMonthlyCalendarRespSuccess,
} from './types';
import { ApiError } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import { queryFetch } from '@services/api';

const useWorkloadMonthlyQuery = () => {
  const { params, parseParams } = useQueryParams();

  const { type, search, unassigned } = params;
  const isPhone = useIsPhone();
  const noOfWeeks = type === 'two-week' || type === 'agenda' ? 2 : undefined;

  return (date: Date) => {
    const queryParams = parseParams(
      {
        date: formatDate(date),
        noOfWeeks,
        displayEndpoint:
          type === 'agenda' ? (isPhone ? 'mobile_agenda_calendar' : 'agenda_calendar') : undefined,
        search,
        unassigned,
      },
      { arrayFormat: 'bracket' },
    );

    return {
      queryKey: noOfWeeks
        ? [...queryKeys.workloadTwoWeeklyCalendar, queryParams]
        : [...queryKeys.workloadMonthlyCalendar, queryParams],
      queryFn: async (): Promise<WorkloadMonthlyCalendarRespSuccess> =>
        await queryFetch({ url: `workloads/monthly${queryParams}` }),
      enabled: ['two-week', 'month', 'agenda'].includes(type || ''),
    };
  };
};

const combineMonthlyRanges = (results: UseQueryResult<WorkloadMonthlyCalendarRespSuccess>[]) => ({
  records: Object.assign(
    {},
    ...results.map((result) => result.data?.records || {}),
  ) as WorkloadMonthlyCalendarItems,
  loading: results.map((result) => result.isPending && result.isFetching),
});

export const useGetWorkloadMonthlyRanges = (starts: Date[]) => {
  const getQuery = useWorkloadMonthlyQuery();

  return useQueries({
    queries: starts.map((start) => getQuery(start)),
    combine: combineMonthlyRanges,
  });
};

export const useGetWorkloadMonthlyCalendar = (date: Date) => {
  const getQuery = useWorkloadMonthlyQuery();

  const { data, error, isPending, isPlaceholderData, isFetching } = useQuery<
    WorkloadMonthlyCalendarRespSuccess,
    ApiError
  >({
    ...getQuery(date),
    placeholderData: (previousData) => previousData as WorkloadMonthlyCalendarRespSuccess,
    select: (d) => {
      const parsedData: WorkloadMonthlyCalendarItems = {};
      const parsedDrafts: WorkloadDraftsItems = {};

      Object.entries(d.records).forEach(([key, records]) => {
        // eslint-disable-next-line immutable/no-mutation
        parsedData[key] = records;
      });
      Object.entries(d.drafts).forEach(([key, records]) => {
        // eslint-disable-next-line immutable/no-mutation
        parsedDrafts[key] = records;
      });

      return { ...d, data: parsedData, drafts: parsedDrafts };
    },
  });

  return {
    data,
    error,
    isLoading: isFetching && (isPending || isPlaceholderData),
  };
};
